// Persistence for the WebRTC developer log: entries are grouped into chunks of
// at most CHUNK_BYTES serialized bytes, and each chunk is one IndexedDB record.

export const CHUNK_BYTES = 500 * 1024;
/** Oldest chunks are pruned beyond this count (about 100 MiB at full chunks). */
export const MAX_STORED_CHUNKS = 200;
const DB_NAME = "r2c2-webrtc-debug";
const STORE_NAME = "chunks";
const LAST_TS_INDEX = "lastTs";
/** History reads stop once this many entries are collected. */
export const HISTORY_ENTRY_LIMIT = 5000;

/** Pseudo-stream holding events that belong to no camera (signaling, etc.). */
export const GENERAL_LOG = "General";

export type DebugLogEntry = {
    /** ISO-8601 UTC timestamp with milliseconds. */
    ts: string;
    kind: string;
    /** Serial of the ARC the event belongs to; absent in entries stored before it was recorded. */
    arcSerial?: string | null;
    cameraKey?: string;
    connectionId?: string;
    [field: string]: unknown;
};

export type DebugLogChunk = {
    /** Identifies one enable-to-disable logging run. */
    runId: string;
    seq: number;
    firstTs: string;
    lastTs: string;
    bytes: number;
    entries: DebugLogEntry[];
};

/** UTF-8 size of the entry's JSON form. */
export const serializedBytes = (entry: DebugLogEntry): number => new Blob([JSON.stringify(entry)]).size;

/**
 * Accumulates entries and emits a chunk as soon as adding the next entry would
 * pass `limitBytes`. A single entry larger than the limit becomes its own chunk.
 */
export class ChunkBuffer {
    private entries: DebugLogEntry[] = [];

    private bytes = 0;

    private seq = 0;

    constructor(
        private readonly runId: string,
        private readonly limitBytes: number = CHUNK_BYTES,
    ) {}

    push(entry: DebugLogEntry): DebugLogChunk | null {
        const size = serializedBytes(entry);
        let full: DebugLogChunk | null = null;
        if (this.entries.length && this.bytes + size > this.limitBytes) {
            full = this.drain();
        }
        this.entries.push(entry);
        this.bytes += size;
        return full;
    }

    /** Emits whatever is buffered, even below the size limit. */
    drain(): DebugLogChunk | null {
        if (!this.entries.length) return null;
        const chunk: DebugLogChunk = {
            runId: this.runId,
            seq: this.seq,
            firstTs: this.entries[0].ts,
            lastTs: this.entries[this.entries.length - 1].ts,
            bytes: this.bytes,
            entries: this.entries,
        };
        this.seq += 1;
        this.entries = [];
        this.bytes = 0;
        return chunk;
    }
}

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

const transactionDone = (transaction: IDBTransaction): Promise<void> =>
    new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });

const openDatabase = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        // v2 adds the lastTs index used by time-range reads; v1 stores are upgraded in place.
        const request = indexedDB.open(DB_NAME, 2);
        request.onupgradeneeded = () => {
            const db = request.result;
            const store = db.objectStoreNames.contains(STORE_NAME)
                ? request.transaction!.objectStore(STORE_NAME)
                : db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
            if (!store.indexNames.contains(LAST_TS_INDEX)) store.createIndex(LAST_TS_INDEX, "lastTs");
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

export const isDebugLogStoreAvailable = (): boolean => typeof indexedDB !== "undefined";

export const writeChunk = async (chunk: DebugLogChunk): Promise<void> => {
    const db = await openDatabase();
    try {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        store.add(chunk);
        const count = await requestToPromise(store.count());
        if (count > MAX_STORED_CHUNKS) {
            const excess = count - MAX_STORED_CHUNKS;
            let removed = 0;
            const cursorRequest = store.openKeyCursor();
            await new Promise<void>((resolve, reject) => {
                cursorRequest.onerror = () => reject(cursorRequest.error);
                cursorRequest.onsuccess = () => {
                    const cursor = cursorRequest.result;
                    if (!cursor || removed >= excess) {
                        resolve();
                        return;
                    }
                    store.delete(cursor.primaryKey);
                    removed += 1;
                    cursor.continue();
                };
            });
        }
        await transactionDone(transaction);
    } finally {
        db.close();
    }
};

export const countStoredChunks = async (): Promise<number> => {
    const db = await openDatabase();
    try {
        return await requestToPromise(db.transaction(STORE_NAME).objectStore(STORE_NAME).count());
    } finally {
        db.close();
    }
};

export const clearStoredChunks = async (): Promise<void> => {
    const db = await openDatabase();
    try {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).clear();
        await transactionDone(transaction);
    } finally {
        db.close();
    }
};

/** Bounds in epoch milliseconds; null leaves that side open. */
export type TimeRange = { fromMs: number | null; toMs: number | null };

const toIso = (ms: number | null): string | null => (ms === null ? null : new Date(ms).toISOString());

/** Entries of one chunk inside the range (ISO-8601 UTC strings order lexicographically). */
export const entriesInRange = (chunk: DebugLogChunk, range: TimeRange): DebugLogEntry[] => {
    const from = toIso(range.fromMs);
    const to = toIso(range.toMs);
    return chunk.entries.filter((entry) => (from === null || entry.ts >= from) && (to === null || entry.ts <= to));
};

/**
 * Reads stored entries inside the range, oldest first. Chunks are scanned
 * newest first and the scan stops once `limit` entries are collected, so a
 * truncated result keeps the most recent entries.
 */
export const readEntriesInRange = async (
    range: TimeRange,
    limit: number = HISTORY_ENTRY_LIMIT,
): Promise<{ entries: DebugLogEntry[]; truncated: boolean }> => {
    const db = await openDatabase();
    try {
        const from = toIso(range.fromMs);
        const to = toIso(range.toMs);
        const index = db.transaction(STORE_NAME).objectStore(STORE_NAME).index(LAST_TS_INDEX);
        // A chunk can only overlap the range if its last entry is not before `from`.
        const bounds = from === null ? undefined : IDBKeyRange.lowerBound(from);
        const collected: DebugLogEntry[] = [];
        await new Promise<void>((resolve, reject) => {
            const request = index.openCursor(bounds, "prev");
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const cursor = request.result;
                if (!cursor || collected.length >= limit) {
                    resolve();
                    return;
                }
                const chunk = cursor.value as DebugLogChunk;
                if (to === null || chunk.firstTs <= to) collected.push(...entriesInRange(chunk, range));
                cursor.continue();
            };
        });
        collected.sort((left, right) => (left.ts < right.ts ? -1 : left.ts > right.ts ? 1 : 0));
        const truncated = collected.length > limit;
        return { entries: truncated ? collected.slice(collected.length - limit) : collected, truncated };
    } finally {
        db.close();
    }
};
